import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take, tap, timer } from 'rxjs';

import { CompanyModel, EmailType, UserViewModel } from '@ninety/mobile/platform/api';
import {
  DatadogLogsService,
  DatadogRumAccount,
  DatadogRumService,
  DatadogRumUser,
} from '@ninety/mobile/platform/services';

import { appActions } from '../../../app/app.actions';
import { AppSelectors } from '../../../app/app.selectors';
import { SessionActions } from '../../session/session.actions';
import { AnalyticsSessionActions } from '../session-cross-link/analytics-session.actions';

const SESSION_POLL_INTERVAL_MS = 1000;
const SESSION_POLL_ATTEMPTS = 10;

/**
 * Syncs the Datadog reserved `usr.*` / `account.*` attributes (RUM and Logs) with the current
 * session, and announces the RUM session for `SessionCrossLinkEffects`. Keyed off lifecycle actions:
 *  - `appAndCompanyReady` sets user/account (re-fires on company switch — a switch triggers a
 *    full WebView reload which re-dispatches ready).
 *  - `logOut` clears them.
 */
@Injectable()
export class DatadogEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly rum = inject(DatadogRumService);
  private readonly logs = inject(DatadogLogsService);

  syncUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.appAndCompanyReady),
        concatLatestFrom(() => this.store.select(AppSelectors.userPersonCompany)),
        filter(([, { user }]) => !!user),
        tap(([, { user, company }]) => {
          const rumUser = this.toRumUser(user);
          const rumAccount = this.toRumAccount(company);
          if (rumUser) {
            this.rum.setUser(rumUser);
            this.logs.setUser(rumUser);
          }
          if (rumAccount) {
            this.rum.setAccount(rumAccount);
            this.logs.setAccount(rumAccount);
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * RUM initializes at bootstrap, outside this module, so its session id is not available the
   * instant the app is ready.
   */
  announceRumSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.appAndCompanyReady),
      switchMap(() =>
        timer(0, SESSION_POLL_INTERVAL_MS).pipe(
          take(SESSION_POLL_ATTEMPTS),
          map(() => (this.rum.isInitialized() ? this.rum.getSessionId() : undefined)),
          filter((sessionId): sessionId is string => !!sessionId),
          take(1),
          map(sessionId => AnalyticsSessionActions.rumSessionDetected({ sessionId }))
        )
      )
    )
  );

  clearOnLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.signedOut),
        tap(() => {
          this.rum.clearUser();
          this.rum.clearAccount();
          this.logs.clearUser();
          this.logs.clearAccount();
        })
      ),
    { dispatch: false }
  );

  private toRumUser(user: UserViewModel | null | undefined): DatadogRumUser | null {
    if (!user) return null;
    const id = user._id ?? user.personId ?? '';
    if (!id) return null;
    const name = user.fullName ?? undefined;
    const email =
      user.primaryEmail ??
      user.emailAddresses?.find(e => e.typeCode === EmailType.main)?.email ??
      user.emailAddresses?.[0]?.email;
    return {
      id,
      name: name ?? undefined,
      email: email ?? undefined,
      personId: user.personId ?? undefined,
      companyId: user.company?.companyId ?? undefined,
    };
  }

  private toRumAccount(company: CompanyModel | undefined | null): DatadogRumAccount | null {
    if (!company) return null;
    const id = company._id ?? company.companyId ?? '';
    if (!id) return null;
    return {
      id,
      name: company.name ?? undefined,
      companyId: company.companyId ?? undefined,
    };
  }
}
